import React, { type ReactNode, useRef, useLayoutEffect } from 'react'
import cx from 'classnames'
import './style.sass'

type TabsProps = {
	children: ReactNode
	className?: string
	containerClassName?: string
	value: string
	onChange: (value: string) => void
	ariaLabel: string
}

export const Tabs = ({ children, className, containerClassName, value, onChange, ariaLabel }: TabsProps): React.ReactNode => {
	const containerclass: string = cx('Tabable', containerClassName)
	const tabs = useRef(document.querySelectorAll('[role="tab"]'))
	const cs: string = cx('Tabs', className)
	let tabFocus = useRef(0)

	const clonedChildren = React.Children.map(children, child =>
		React.cloneElement(child as React.ReactElement<any>, {
			value: value,
			onChange: onChange,
		}),
	)

	useLayoutEffect(() => {
		if (tabs.current.length === 0) {
			tabs.current = document.querySelectorAll('[role="tab"]')

			for (let idx = 0; idx < tabs.current.length; idx++) {
				if (tabs.current[idx].id === value) {
					tabFocus.current = idx
					break
				}
			}
		}
	})

	const handleKeyDownEvent = (e: React.KeyboardEvent<HTMLDivElement>) => {
		// Move right
		if (e.keyCode === 39 || e.keyCode === 37) {
			tabs.current[tabFocus.current].setAttribute('tabindex', '-1')
			if (e.keyCode === 39) {
				tabFocus.current++
				// If we're at the end, go to the start
				if (tabFocus.current >= tabs.current.length) {
					tabFocus.current = 0
				}
				// Move left
			} else if (e.keyCode === 37) {
				tabFocus.current--
				// If we're at the start, move to the end
				if (tabFocus.current < 0) {
					tabFocus.current = tabs.current.length - 1
				}
			}

			tabs.current[tabFocus.current].setAttribute('tabindex', '0')
			;(tabs.current[tabFocus.current] as HTMLElement).focus()
		}
	}
	return (
		<div className={containerclass}>
			<div className={cs} role='tablist' aria-label={ariaLabel} onKeyDown={handleKeyDownEvent}>
				{clonedChildren}
			</div>
		</div>
	)
}

type SingleTabProps = {
	id: string
	onChange?: (value: string) => void
	className?: string
	clear?: boolean
	value?: string
	children: ReactNode
	[key: `data-${string}`]: string | undefined
}
export const Tab = ({ id, onChange, className, children, clear, value, ...rest }: SingleTabProps): React.ReactNode => {
	const cs: string = cx('Tab', className, { 'Tab--active': value === id, 'Tab--clear': Boolean(clear) })

	const onClick = () => {
		if (onChange) onChange(id)
	}

	return (
		<button
			{...rest}
			onClick={onClick}
			className={cs}
			role='tab'
			id={id}
			aria-selected={value === id}
			tabIndex={Number(value === id) - 1}>
			{children}
		</button>
	)
}

type TabPanelProps = {
	value: string
	tabPanelValue: string
	children: ReactNode
	variants?: Object
}

export const TabPanel = ({ tabPanelValue, value, children }: TabPanelProps): React.ReactNode => {
	return (
		<div
			className='TabPanel__Container'
			role='tabpanel'
			tabIndex={Number(value === tabPanelValue) - 1}
			aria-labelledby={tabPanelValue}>
			{children}
		</div>
	)
}
